// NPM imports
import React, { useState, useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Button, Link } from '@americanexpress/dls-react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useFetchye } from 'fetchye';

// Local imports
import AssertionVerify from './AssertionVerify/AssertionVerify';
import AssertionSuccess from './AssertionSuccess/AssertionSuccess';
import { usePasskeyAssertion } from '../../hooks/usePasskey';
import buildFetchOptions from '../../utils/fetchOptions';
import ENDPOINTS from '../../utils/endpoints';
import devLog from '../../utils/devLog';
import styles from './PasskeyAssertion.scss';

const SAFEKEY_LOGO_URL = 'https://www.aexp-static.com/cdaas/one/statics/axp-static-assets/2.24.1/package/dist/img/logos/dls-logo-line.svg';

const PasskeyAssertion = ({ sessionData = {}, onContinue = undefined }) => {
  const intl = useIntl();
  const {
    sessionId,
    challenge,
    credentialIds,
    token,
  } = sessionData || {};

  const bffApiUrl = useSelector((state) => state.getIn(['config', 'bffApiUrl']));
  const { CEREMONY_SESSIONS, CEREMONY_COMPLETE } = ENDPOINTS;

  const {
    isAuthenticating, handleAuthenticate,
  } = usePasskeyAssertion({
    challenge,
    credentialId: credentialIds || [],
  });

  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [completionParams, setCompletionParams] = useState(null);

  const completionUrl = completionParams
    ? `${bffApiUrl}${CEREMONY_SESSIONS}/${sessionId}${CEREMONY_COMPLETE}`
    : false;

  const { run: postAssertionComplete } = useFetchye(
    completionUrl,
    buildFetchOptions({
      body: completionParams?.body || {},
      defer: true,
      headers: { sessionId, Authorization: `Bearer ${token}` },
    })
  );

  useEffect(() => {
    if (!completionParams) return;
    const completeAssertion = async () => {
      try {
        await postAssertionComplete();
        setIsVerifying(false);
        setCompletionParams(null);
        setIsVerified(true);
        if (onContinue) onContinue();
      } catch (err) {
        devLog.error('Assertion completion failed:', err);
        setIsVerifying(false);
        setCompletionParams(null);
      }
    };
    completeAssertion();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when completionParams changes
  }, [completionParams]);

  const onAuthenticate = useCallback(async () => {
    setIsVerifying(true);
    setHasError(false);
    try {
      const assertionPayload = await handleAuthenticate();
      setCompletionParams({ body: assertionPayload });
    } catch (err) {
      devLog.error('Passkey assertion failed:', err?.name, err?.message);
      setIsVerifying(false);
      setHasError(true);
    }
  }, [handleAuthenticate]);

  if (isVerified) {
    return <AssertionSuccess />;
  }

  return (
    <div className={styles['passkey-assertion']}>
      <div className={styles['passkey-assertion__header']}>
        <span className={styles['passkey-assertion__bank-name']}>
          {intl.formatMessage({ id: 'passkeyAssertion.headerBankName' })}
        </span>
        <img
          src={SAFEKEY_LOGO_URL}
          alt={intl.formatMessage({ id: 'passkeyAssertion.safeKeyLogoAlt' })}
          className={styles['passkey-assertion__safekey-logo']}
        />
      </div>

      <div className={styles['passkey-assertion__content']}>
        {hasError && !isVerifying && !isAuthenticating && (
          <div>
            <p className={styles['passkey-assertion__description']}>
              {intl.formatMessage({ id: 'passkeyAssertion.error.message' })}
            </p>
            <div className={styles['passkey-assertion__actions']}>
              <Button
                variant="primary"
                onClick={onAuthenticate}
              >
                {intl.formatMessage({ id: 'passkeyAssertion.error.retryWithPasskey' })}
              </Button>
              {onContinue && (
                <Button
                  variant="secondary"
                  onClick={onContinue}
                >
                  {intl.formatMessage({ id: 'passkeyAssertion.error.continueWithoutPasskey' })}
                </Button>
              )}
            </div>
          </div>
        )}
        {!hasError && !isVerifying && !isAuthenticating && (
          <div>
            <p className={styles['passkey-assertion__description']}>
              {intl.formatMessage({ id: 'passkeyAssertion.verifyMessage' })}
            </p>
            <div className={styles['passkey-assertion__actions']}>
              <Button
                onClick={onAuthenticate}
                disabled={isVerifying || isAuthenticating}
              >
                {intl.formatMessage({ id: 'passkeyAssertion.continueButton' })}
              </Button>
            </div>
          </div>
        )}
        {(isVerifying || isAuthenticating) && <AssertionVerify />}
      </div>
      <div className={styles['passkey-assertion__privacy-link']}>
        <Link
          href={intl.formatMessage({ id: 'passkeyAssertion.privacyLinkUrl' })}
        >
          {intl.formatMessage({ id: 'passkeyAssertion.privacyLink' })}
        </Link>
      </div>
    </div>
  );
};

PasskeyAssertion.propTypes = {
  sessionData: PropTypes.shape({
    sessionId: PropTypes.string.isRequired,
    challenge: PropTypes.string.isRequired,
    credentialIds: PropTypes.arrayOf(PropTypes.string),
    token: PropTypes.string.isRequired,
  }),
  onContinue: PropTypes.func,
};

export default PasskeyAssertion;
